import React from 'react';
import { Spinner } from 'react-bootstrap';
import { PatientService, ReminderContext, ReminderContextType } from '~/services';
import { formatNumber, useServiceSubscribe } from '~/util';
import { useReminderQuery } from '~/components/Reminders';

export function ReminderStatistics() {
    const { filterValues, setFilterValues } = React.useContext(
        ReminderContext
    ) as ReminderContextType;
    const { getReminderAggregates } = useReminderQuery();
    const [loading, setLoading] = React.useState(false);
    const [statistics, setStatistics] = React.useState({
        remindersTotal: 0,
        statusOpen: 0,
        statusInProgress: 0,
        statusDone: 0,
        statusNA: 0,
        statusMissed: 0,
        openGaps: 0,
        warning: 0,
        critical: 0,
    });
    type StatisticType = keyof typeof statistics;

    const loadReminderAggregates = React.useCallback(async () => {
        setLoading(true);
        const result = await getReminderAggregates();
        setStatistics((prev) => {
            const updatedCategories = { ...prev };
            updatedCategories.remindersTotal = result.data.total;
            updatedCategories.statusOpen = result.data.open;
            updatedCategories.statusInProgress = result.data.in_progress;
            updatedCategories.statusDone = result.data.done;
            updatedCategories.statusNA = result.data.dismissed;
            updatedCategories.statusMissed = result.data.missed;
            updatedCategories.openGaps = result.data.open_gaps;
            updatedCategories.warning = result.data.warning;
            updatedCategories.critical = result.data.critical;
            return updatedCategories;
        });
        setLoading(false);
    }, [getReminderAggregates]);

    React.useEffect(() => {
        loadReminderAggregates();
    }, [loadReminderAggregates]);

    useServiceSubscribe(PatientService.EVENTS.reminders_updated, loadReminderAggregates);

    function handlePillFilter(statistic: StatisticType) {
        setFilterValues((prev) => {
            const updatedFilters = { ...prev };
            if (statistic === 'remindersTotal') {
                updatedFilters.viewStatus = [];
                updatedFilters.priority = null;
                updatedFilters.hasOpenGap = false;
            }
            if (statistic.substring(0, 6) === 'status')
                updatedFilters.viewStatus = [statistic.substring(6).toLowerCase()];
            if (statistic === 'openGaps') {
                updatedFilters.viewStatus = ['done'];
                updatedFilters.createdByType = 'system';
                updatedFilters.priority = null;
                updatedFilters.hasOpenGap = true;
            }
            if (statistic === 'critical' || statistic === 'warning') {
                updatedFilters.viewStatus = ['open'];
                updatedFilters.createdByType = 'system';
                updatedFilters.hasOpenGap = false;
                updatedFilters.priority = statistic;
            }
            return updatedFilters;
        });
    }

    function getActiveBadge(statistic: StatisticType) {
        let isActive = false;
        if (statistic === 'remindersTotal') isActive = false;
        if (
            (statistic === 'critical' || statistic === 'warning') &&
            filterValues.priority === statistic
        )
            isActive = true;
        if (statistic === 'openGaps' && filterValues.hasOpenGap) isActive = true;
        if (
            statistic.substring(0, 6) === 'status' &&
            filterValues.viewStatus.includes(statistic.substring(6).toLowerCase())
        )
            isActive = true;
        return isActive ? 'badge-outline-' : 'bg-';
    }

    function displayPill(
        item: StatisticType,
        label: string,
        title: string,
        className: string,
        spinner = false
    ) {
        return (
            <div className="badge-col col mb-2 mb-lg-0">
                <span className="d-none d-lg-inline text-nowrap">
                    {label}
                    <br />
                </span>
                <button
                    title={title}
                    type="button"
                    className={`badge rounded-pill ${getActiveBadge(item)}${className}`}
                    onClick={() => handlePillFilter(item)}
                >
                    {loading && spinner && (
                        <Spinner animation="border" size="sm" role="status" variant="secondary">
                            <span className="sr-only">Loading Count</span>
                        </Spinner>
                    )}
                    {loading && !spinner && '--'}
                    {!loading && formatNumber(statistics[item])}
                </button>
            </div>
        );
    }

    return (
        <div className="row mb-4 pb-3 flex-column flex-lg-row text-center" id="statistics">
            <div className="col-12 col-lg-1">
                <div className="row badge-row flex-nowrap flex-lg-wrap justify-content-lg-center">
                    <div className="col-3 col-lg-12 title text-end text-lg-center">Reminders</div>
                    {displayPill(
                        'remindersTotal',
                        'Total',
                        'Number of reminders based on current search criteria',
                        'light',
                        true
                    )}
                </div>
            </div>
            <div className="col-12 col-lg-4 offset-lg-2">
                <div className="row badge-row flex-nowrap flex-lg-wrap justify-content-lg-center">
                    <div className="col-3 col-lg-12 title text-end text-lg-center">Status</div>
                    {displayPill(
                        'statusOpen',
                        'Open',
                        'Number of reminders in the Open status',
                        'info'
                    )}
                    {displayPill(
                        'statusInProgress',
                        'In Progress',
                        'Number of reminders in the In Progress status',
                        'purple'
                    )}
                    {displayPill(
                        'statusDone',
                        'Done',
                        'Number of reminders in the Done status',
                        'success'
                    )}
                    {displayPill(
                        'statusNA',
                        'N/A',
                        'Number of reminders in the N/A status',
                        'secondary'
                    )}
                    {displayPill(
                        'statusMissed',
                        'Missed',
                        'Number of reminders in the Missed status',
                        'secondary'
                    )}
                </div>
            </div>
            <div className="col-12 col-lg-4">
                <div className="row badge-row inprogress-row flex-nowrap flex-lg-wrap justify-content-lg-center">
                    <div className="col-3 col-lg-12 title text-end text-lg-center">Priority</div>
                    {displayPill(
                        'openGaps',
                        'Open Gaps',
                        'Number of reminders with an associated measure',
                        'purple'
                    )}
                    {displayPill(
                        'warning',
                        'Warning',
                        'Number of reminders approaching the due date',
                        'warning'
                    )}
                    {displayPill(
                        'critical',
                        'Critical',
                        'Number of reminders that require immediate attention',
                        'danger'
                    )}
                </div>
            </div>
        </div>
    );
}
